import { useLocale, useT } from '../i18n/locale-context';
import { localeCount, tendersWord } from '../lib/format';
import type { CompanyAwardBreakdown, CompanyPayoff } from '../lib/auth-client';

/**
 * The one renderer for what an ИНН submission found.
 *
 * Two surfaces show this — the settings/modal card and the onboarding wizard's
 * ИНН step — and they each had their own copy of the same four-branch switch.
 * Predictably, they drifted: adding the `{{word}}` interpolation that fixes
 * Russian's plural agreement («2 тендера», not «2 тендеров») updated one caller
 * and left the other rendering the literal text `{{word}}` on screen to real
 * users.
 *
 * A shared component is the fix, not a discipline problem. Any future change to
 * the payoff — a new branch, another interpolation — now lands in both places
 * by construction.
 */
export function CompanyPayoffLine({ payoff }: { payoff: CompanyPayoff }) {
  const t = useT();
  const { locale } = useLocale();

  // Spain (`keepsTypedName`): a company saved by the name the reader typed —
  // with no NIF (`named`), or beside a NIF nobody's register knows (`none`).
  if (payoff.kind === 'named') return <>{t('company.savedName', { name: payoff.typedName })}</>;

  // `none` deliberately reports no count. A branch that resolves to zero falls
  // through to the next one, and when nothing resolves at all, silence beats
  // telling somebody we found 0 about them.
  if (payoff.kind === 'none') {
    return (
      <>
        {payoff.typedName
          ? t('company.savedWithName', { name: payoff.typedName })
          : t('company.saved')}
      </>
    );
  }

  // Spain (`companyId.payoff: 'es'`): every award, split, and the sector most
  // of them sit in — «Tu empresa ha ganado 12 contratos (3 licitaciones y 9
  // menores), sobre todo en servicios de limpieza». Only the API on Spain sends
  // a breakdown, so UZ/KZ never reach this branch.
  if (payoff.kind === 'wins' && payoff.breakdown) {
    const sectorName = payoff.sector?.name ? midSentence(payoff.sector.name) : null;
    const open = payoff.sector?.open ?? 0;
    return (
      <>
        {t('company.foundAwards', {
          what: esAwardsPhrase(payoff.count, payoff.breakdown),
          sector: sectorName ? t('company.foundAwards.sector', { name: sectorName }) : '',
        })}
        {open > 0 && (
          <>
            {' '}
            {t('company.foundAwards.open', {
              count: localeCount(open),
              word: tendersWord(open, locale),
            })}
          </>
        )}
      </>
    );
  }

  const word = tendersWord(payoff.count, locale);
  const key =
    payoff.kind === 'wins'
      ? 'company.foundWins'
      : payoff.kind === 'sector'
        ? 'company.foundSector'
        : 'company.foundRegion';

  return <>{t(key, { n: payoff.count, word })}</>;
}

/**
 * «12 contratos (3 licitaciones y 9 menores)» — how many awards and how they
 * split, in Spanish (the only deployment whose API sends a breakdown). One
 * kind alone is named outright: «9 contratos menores», «3 licitaciones»;
 * `other` (negotiated without publicity, or no procedure on record) is «otros».
 */
export function esAwardsPhrase(count: number, b: CompanyAwardBreakdown): string {
  const n = (x: number): string => String(localeCount(x));
  const parts: string[] = [];
  if (b.competitive > 0)
    parts.push(`${n(b.competitive)} ${b.competitive === 1 ? 'licitación' : 'licitaciones'}`);
  if (b.minor > 0) parts.push(`${n(b.minor)} ${b.minor === 1 ? 'menor' : 'menores'}`);
  // Chile: the Compras Ágiles of Mercado Público (`agile` — the API sends it on cl only).
  const agile = b.agile ?? 0;
  if (agile > 0) parts.push(`${n(agile)} ${agile === 1 ? 'Compra Ágil' : 'Compras Ágiles'}`);
  if (b.other > 0) parts.push(`${n(b.other)} ${b.other === 1 ? 'otro' : 'otros'}`);
  const contracts = `${n(count)} ${count === 1 ? 'contrato' : 'contratos'}`;
  if (parts.length > 1) {
    const list = `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
    return `${contracts} (${list})`;
  }
  if (b.competitive > 0) return parts[0]!;
  if (b.minor > 0) return `${n(b.minor)} ${b.minor === 1 ? 'contrato menor' : 'contratos menores'}`;
  if (agile > 0) return parts[0]!;
  return contracts;
}

/** A CPV division name in the middle of a sentence: «Servicios de …» →
 *  «servicios de …», leaving an acronym («TIC …») as it is. */
export function midSentence(name: string): string {
  const [first = '', second = ''] = [...name];
  return second && second === second.toLowerCase() ? first.toLowerCase() + name.slice(1) : name;
}
